import { conditionSymbol } from "@/utility/conditionalExpressions";
import { t } from "@lingui/core/macro";
import { AlertIcon, Button, Toast } from "@mm/metx-design-system";
import { createPortal } from "react-dom";
import type { AlertNotification } from "../api";
import { alertIconVariant } from "../utils";
import "./notification-toast.scss";

type Props = {
  items: AlertNotification[];
  onDismiss: (id: string) => void;
  onOpen: (item: AlertNotification) => void;
};

export function NotificationToast({ items, onDismiss, onOpen }: Props) {
  return createPortal(
    <div className="notification-toast">
      {items.map((item) => (
        <Toast
          key={item.id}
          elevated
          fullWidth
          variant="neutral"
          showAction={item.has_destination}
          duration={10000}
          closeLabel={t`Dismiss`}
          onClose={() => onDismiss(item.id)}
          leading={<AlertIcon variant={alertIconVariant(item.severity)} />}
          title={item.title}
          action={
            <Button size="sm" onClick={() => onOpen(item)}>
              {t`View Dashboard`}
            </Button>
          }
        >
          <NotificationToastContent item={item} />
        </Toast>
      ))}
    </div>,
    document.body,
  );
}

function NotificationToastContent({ item }: { item: AlertNotification }) {
  return (
    <span className="notification-toast__content">
      <span>{item.locations.join(", ")}</span>
      <span>
        {item.conditions?.map((condition) => (
          <span
            key={`${condition.parameter}:${condition.operator}:${condition.threshold}`}
            className="notification-toast__metric"
          >
            <strong>{condition.parameter}</strong>
            <span>{`${conditionSymbol(condition.operator)} ${condition.threshold}`}</span>
            {item.ranges
              ?.filter((span) => span.parameter === condition.parameter)
              .map((span) => (
                <span key={`${span.parameter}:${span.minimum}:${span.maximum}`}>
                  {`(min: ${span.minimum}, max: ${span.maximum})`}
                </span>
              ))}
          </span>
        ))}
      </span>
    </span>
  );
}
